import { getAccountCodesByBudgetId, getFunds } from './accountService.js';
import { getDepartmentsAndDivisions } from './programService.js';
import { getAllResultTypes } from './resultsService.js';
import { getSetById } from './insightsService.js';
import { getBudgets } from './budgetService.js';
import {
  getBudgeterObjectCategoryTypes,
  getBudgeterOrganizationLevels,
  getRequestGroupNames,
} from './budgeterService.js';
import { getItemNamesByTypes } from './dataVisualizationsService.js';
import {
  getNonPersonnelFilters,
  getPersonnelFilters,
  getOrganizationLevelsByCostCenters,
  getFiltersForCostAllocation,
} from './costModelService.js';
import {
  COST_TYPES,
  ITEM_OBJECT_CATEGORIES_DELIMITER,
  QUARTILE_DICTIONARY,
  PERSONNEL_OBJECT_TYPE_ID,
  NONPERSONNEL_OBJECT_TYPE_ID,
  ACCOUNT_TYPE_REVENUE_ID,
  ACCOUNT_TYPE_EXPENSE_ID
} from '../static/constants.js';
import { REQUEST_TYPES } from '../components/BudgetRequests/budgetRequestsHelpers.js';
import { FILTER_TYPES } from '../components/reusable/Filters/filterHelpers.js';
import { ToastComponent } from '@tylertech/forge';
import {
  SIMPLE,
  COMPOUND,
  COMMUNITY,
  GOVERNANCE,
  BPA,
  EXPENSE,
  REVENUE,
  CREATED,
  REFINED,
} from '../components/../static/keyWords.js';
import { messageManager } from '../components/System/messageManager/messageManager.js';

async function getFundFilterData(nameConventions) {
  let funds = [];

  try {
    funds = (await getFunds()).data;
  } catch {
    ToastComponent.present({ message: messageManager(nameConventions).SOMETHING_WENT_WRONG });
  }

  return { data: funds, filterType: SIMPLE };
}

const getDepartmentFilterData = async (divisionsFilter, nameConventions) => {
  let departmentsAndDivisions = [];

  try {
    departmentsAndDivisions = (
      await getDepartmentsAndDivisions({
        divisionsFilter: divisionsFilter ? divisionsFilter.value : [],
        departmentsFilter: [],
      })
    ).data;
  } catch {
    ToastComponent.present({ message: messageManager(nameConventions).SOMETHING_WENT_WRONG });
  }

  return {
    data: departmentsAndDivisions.departments,
    filterType: SIMPLE,
    isLinked: true,
  };
};

const getDivisionFilterData = async (departmentsFilter, nameConventions) => {
  let departmentsAndDivisions = [];

  try {
    departmentsAndDivisions = (
      await getDepartmentsAndDivisions({
        divisionsFilter: [],
        departmentsFilter: departmentsFilter ? departmentsFilter.value : [],
      })
    ).data;
  } catch {
    ToastComponent.present({ message: messageManager(nameConventions).SOMETHING_WENT_WRONG });
  }

  return {
    data: departmentsAndDivisions.divisions,
    filterType: SIMPLE,
    isLinked: true,
  };
};

const getBPAFilterData = async (nameConventions) => {
  let bpaFilterData = [];

  try {
    bpaFilterData = (await getAllResultTypes()).data.find(
      (resultType) => resultType.name === BPA
    ).results;
  } catch {
    ToastComponent.present({ message: messageManager(nameConventions).SOMETHING_WENT_WRONG });
  }
  return { data: bpaFilterData, filterType: COMPOUND };
};

const getResultFilterData = async (nameConventions) => {
  let resultsFilterData = [];

  try {
    let results = (await getAllResultTypes()).data.filter(
      (resultType) => resultType.name !== BPA
    );
    results.forEach((result) => {
      resultsFilterData.push(result.results);
    });
  } catch {
    ToastComponent.present({ message: messageManager(nameConventions).SOMETHING_WENT_WRONG });
  }
  return { data: resultsFilterData.flat(), filterType: COMPOUND };
};

const getServiceTypeFilterData = () => {
  return {
    data: [
      { id: 1, name: COMMUNITY },
      { id: 2, name: GOVERNANCE },
    ],
    filterType: SIMPLE,
  };
};

const getPersonnelFiltersForCostAllocation = async (filterType, nameConventions) => {
  let organizationLevels = [];
  let objectTypeId =
    filterType === FILTER_TYPES.COST_ALLOCATION_PERSONNEL
      ? PERSONNEL_OBJECT_TYPE_ID
      : NONPERSONNEL_OBJECT_TYPE_ID;
  let accountTypeId =
    filterType === FILTER_TYPES.COST_ALLOCATION_REVENUE
      ? ACCOUNT_TYPE_REVENUE_ID
      : ACCOUNT_TYPE_EXPENSE_ID;

  try {
    const result = await getFiltersForCostAllocation(
      objectTypeId,
      accountTypeId
    );
    if (result.status === 200) {
      organizationLevels.push(...(result.data || []));
    }
  } catch {
    ToastComponent.present({
      message: messageManager(nameConventions).SOMETHING_WENT_WRONG,
    });
  }

  return { data: organizationLevels, filterType: COMPOUND };
};

const getOrganizationLevelsForBudgeter = async (activeBudget, nameConventions) => {
  let organizationLevels = [];

  try {
    if (activeBudget == 'null') return [];
    const result = await getBudgeterOrganizationLevels(true);
    if (result.status === 200) {
      organizationLevels.push(
        ...(result.data || [])
          .map((element) => {
            return {
              abbreviation: element.abbreviation,
              name: element.name,
              id: element.id,
              definitions: element.definitions,
              scoreOptions: element.definitions,
              type: 'orgLevel',
              level: element.id,
            };
          })
          .sort((a, b) => a.id - b.id)
      );
    }
  } catch {
    ToastComponent.present({
      message: messageManager(nameConventions).SOMETHING_WENT_WRONG,
    });
  }

  return { data: organizationLevels, filterType: COMPOUND };
};

const getBudgeterCategoryTypes = async (objectTypeId, activeBudget, nameConventions) => {
  let categoryTypes = [];

  try {
    if (activeBudget == 'null') return [];
    const result = await getBudgeterObjectCategoryTypes(objectTypeId, true);
    if (result.status === 200) {
      categoryTypes.push(
        ...(result.data || []).map((element, index) => {
          return {
            abbreviation: element.abbreviation,
            name: element.name,
            id: element.id,
            definitions: element.definitions,
            scoreOptions: element.definitions,
            type: 'objCategory',
            level: index + 1,
          };
        })
      );
    }
  } catch {
    ToastComponent.present({
      message: messageManager(nameConventions).SOMETHING_WENT_WRONG,
    });
  }

  return { data: categoryTypes, filterType: COMPOUND };
};

const QUARTILE_FILTER_VALUES = Object.values(QUARTILE_DICTIONARY).reduce(
  (accumulator, currentValue, currentIndex) => {
    if (currentIndex !== 0) {
      accumulator.push({
        id: currentIndex,
        name: currentValue,
      });
    }
    return accumulator;
  },
  []
);

const getQuartileFilterData = () => {
  return { data: QUARTILE_FILTER_VALUES, filterType: SIMPLE };
};

const QUARTILE_DIFFS_FILTER_VALUES = [
  { name: '-3', id: 1 },
  { name: '-2', id: 2 },
  { name: '-1', id: 3 },
  { name: '0', id: 4 },
  { name: '1', id: 5 },
  { name: '2', id: 6 },
  { name: '3', id: 7 },
];

const getQuartileDiffs = () => {
  return { data: QUARTILE_DIFFS_FILTER_VALUES, filterType: SIMPLE };
};

const OFFSET_FILTER_VALUES = [
  { id: 1, name: 'Offset' },
  { id: 2, name: 'Not offset' },
];

const getOffsetFilterData = () => {
  return { data: OFFSET_FILTER_VALUES, filterType: SIMPLE };
};

const COST_TYPES_FILTER_VALUES = COST_TYPES.map((costType, index) => {
  // 0th index is reserved for 'Select All option'
  return {
    id: index + 1,
    name: costType,
  };
});

const getCostTypeFilterData = () => {
  return { data: COST_TYPES_FILTER_VALUES, filterType: SIMPLE };
};

const getAccountTypeFilterData = () => {
  return {
    data: [
      { id: 2, name: EXPENSE },
      { id: 4, name: REVENUE },
    ],
    filterType: SIMPLE,
  };
};

const getQuestionsFilterData = () => {
  return {
    data: [
      { id: 1, name: 'All Questions Answered' },
      { id: 2, name: 'Partial Questions Answered' },
      { id: 3, name: 'No Questions Answered' },
    ],
    filterType: SIMPLE,
  };
};

const getDurationTypeFilterData = () => {
  return {
    data: [
      { id: 2, name: 'Ongoing' },
      { id: 1, name: 'One time' },
    ],
    filterType: SIMPLE,
  };
};

// Will update this logic once separate endpoints are made
const getComplexityFilterData = async (set) => {
  if (!set) return [];
  const { data } = await getSetById(set.id);
  return {
    data: data.insightBoxChoiceTypes[0].insightBoxChoices,
    filterType: SIMPLE,
  };
};

const getTimelineFilterData = async (set) => {
  if (!set) return [];
  const { data } = await getSetById(set.id);
  return {
    data: data.insightBoxChoiceTypes[1].insightBoxChoices,
    filterType: SIMPLE,
  };
};

const getProgramChangeFilterData = async (set) => {
  if (!set) return [];
  const { data } = await getSetById(set.id);
  return { data: data.programChangeTypes, filterType: SIMPLE };
};

const getDVDivisionsFilterData = async (secondLevels, nameConventions) => {
  let divisions = [];
  const otherLevelSelectedItems = secondLevels
    ? (secondLevels.value || []).map((element) => element.name)
    : null;

  try {
    const result = await getOrganizationLevelsByCostCenters(
      1,
      2,
      otherLevelSelectedItems
    );

    if (result.status === 200) {
      divisions.push(
        ...(result.data || [])
          .sort((a, b) => a.name.localeCompare(b.name))
          .map((element, index) => {
            return {
              id: index + 1,
              name: element.name,
            };
          })
      );
    }
  } catch {
    ToastComponent.present({ message: messageManager(nameConventions).SOMETHING_WENT_WRONG });
  }

  return { data: divisions, filterType: SIMPLE, isLinked: true };
};

const getDVSectionsFilterData = async (firstLevels, nameConventions) => {
  let sections = [];
  const otherLevelSelectedItems = firstLevels
    ? (firstLevels.value || []).map((element) => element.name)
    : null;

  try {
    const result = await getOrganizationLevelsByCostCenters(
      2,
      1,
      otherLevelSelectedItems
    );

    if (result.status === 200) {
      sections.push(
        ...(result.data || [])
          .sort((a, b) => a.name.localeCompare(b.name))
          .map((element, index) => {
            return {
              id: index + 1,
              name: element.name,
            };
          })
      );
    }
  } catch {
    ToastComponent.present({ message: messageManager(nameConventions).SOMETHING_WENT_WRONG });
  }

  return { data: sections, filterType: SIMPLE, isLinked: true };
};

const getDVQuartileFilterData = () => {
  return {
    data: Object.values(QUARTILE_DICTIONARY).map((value, index) => {
      return {
        id: index + 1,
        name: value,
      };
    }),
    filterType: SIMPLE,
  };
};

const getDVPersonnelFilterData = async (nameConventions) => {
  let personnel = [];

  try {
    // TODO: try to pass down budget id since data is tied to it
    const result = await getPersonnelFilters();
    if (result.status === 200) {
      personnel.push(
        ...(result.data || []).map((element, index) => {
          return {
            id: index + 1,
            name: element.name, // DO NOT ASK ME WHY TYPEORM DOES THIS NAMING WITH A SUBSTRING FUNCTION ON A COLUMN
          };
        })
      );
    }
  } catch {
    ToastComponent.present({
      message: messageManager(nameConventions).SOMETHING_WENT_WRONG,
    });
  }

  const result = personnel.sort((a, b) =>
    a.name.toLowerCase().localeCompare(b.name.toLowerCase())
  );
  return { data: result, filterType: SIMPLE };
};

const getDVNonPersonnelFilterData = async (nameConventions) => {
  let nonPersonnel = [];

  try {
    // TODO: try to pass down budget id since data is tied to it
    const result = await getNonPersonnelFilters();

    if (result.status === 200) {
      nonPersonnel.push(
        ...(result.data || []).map((element, index) => {
          return {
            id: index + 1,
            name: element.name, // DO NOT ASK ME WHY TYPEORM DOES THIS NAMING WITH A SUBSTRING FUNCTION ON A COLUMN
          };
        })
      );
    }
  } catch {
    ToastComponent.present({
      message: messageManager(nameConventions).SOMETHING_WENT_WRONG,
    });
  }

  return { data: nonPersonnel, filterType: SIMPLE };
};

const getDVRevenueFilterData = async (nameConventions) => {
  let revenue = [];

  try {
    // TODO: try to pass down budget id since data is tied to it
    const itemNames = await getItemNamesByTypes();

    if (itemNames.status === 200) {
      let revenueItemNames = itemNames.data.revenue.map(
        (itemName) => itemName.split(ITEM_OBJECT_CATEGORIES_DELIMITER)[0]
      );
      let uniqueRevenueItemNames = new Set(revenueItemNames);
      revenue.push(
        ...Array.from(uniqueRevenueItemNames).map((element, index) => {
          return {
            id: index + 1,
            name: element,
          };
        })
      );
    }
  } catch {
    ToastComponent.present({ message: messageManager(nameConventions).SOMETHING_WENT_WRONG });
  }

  return { data: revenue, filterType: SIMPLE };
};

const getDVBudgetsFilterData = async (organizationBudgets, nameConventions) => {
  let budgets = [];

  try {
    const result = await getBudgets();

    if (result.status === 200) {
      budgets.push(
        ...(result.data || []).map((budget) => {
          return {
            id: budget.id,
            name: `${budget.name}-${budget.year}`,
          };
        })
      );
    }
  } catch {
    ToastComponent.present({ message: messageManager(nameConventions).SOMETHING_WENT_WRONG });
  }

  budgets = budgets.filter((budget) =>
    organizationBudgets.find((orgBudget) => orgBudget.id === budget.id)
  );
  return { data: budgets, filterType: SIMPLE };
};

const getRequestsStatusTypesData = () => {
  return {
    data: [
      { id: 1, name: 'Pending' },
      { id: 2, name: 'Approved' },
      { id: 3, name: 'Rejected' },
    ],
    filterType: SIMPLE,
  };
};

const getRequestTypeFilterData = () => {
  return {
    data: [
      { id: 1, name: REQUEST_TYPES.INCREASE_ITEM_COST },
      { id: 2, name: REQUEST_TYPES.DECREASE_ITEM_COST },
      { id: 5, name: REQUEST_TYPES.CREATE_NEW_ITEM_FROM_EXISTING },
    ],
    filterType: SIMPLE,
  };
};

const getAccountCodeFilterData = async (activeBudget) => {
  if (activeBudget === 'null') {
    return [];
  } else {
    const accCodes = await getAccountCodesByBudgetId(activeBudget.id);
    return { data: accCodes.data, filterType: SIMPLE };
  }
};

const getBudgeterGroupsFilterData = async (activeBudget) => {
  if (activeBudget === 'null') {
    return [];
  } else {
    const groups = await getRequestGroupNames(activeBudget.id);
    return { data: groups.data, filterType: SIMPLE };
  }
};

const getStatusFilterData = () => {
  return {
    data: [
      { id: 1, name: CREATED },
      { id: 2, name: REFINED },
    ],
    filterType: SIMPLE,
  };
};

const getBenchmarkCostFilters = () => {
  return {
    data: [
      {id: 1, name: 'Low'},
      {id: 2, name: 'Typical'},
      {id: 3, name: 'High'},
    ],
    filterType: SIMPLE
  };
};

export {
  getFundFilterData,
  getDepartmentFilterData,
  getDivisionFilterData,
  getBPAFilterData,
  getResultFilterData,
  getServiceTypeFilterData,
  getPersonnelFiltersForCostAllocation,
  getOrganizationLevelsForBudgeter,
  getBudgeterCategoryTypes,
  getQuartileFilterData,
  getOffsetFilterData,
  getCostTypeFilterData,
  getAccountTypeFilterData,
  getQuestionsFilterData,
  getDurationTypeFilterData,
  getComplexityFilterData,
  getTimelineFilterData,
  getProgramChangeFilterData,
  getDVDivisionsFilterData,
  getDVSectionsFilterData,
  getDVQuartileFilterData,
  getDVPersonnelFilterData,
  getDVNonPersonnelFilterData,
  getDVRevenueFilterData,
  getDVBudgetsFilterData,
  getRequestsStatusTypesData,
  getRequestTypeFilterData,
  getAccountCodeFilterData,
  getBudgeterGroupsFilterData,
  getQuartileDiffs,
  getStatusFilterData,
  getBenchmarkCostFilters
};
